/**
 * WebSocket-клиент.
 *
 * Отвечает за переподключение, повторную подписку и доставку событий
 * подписчикам. Прав не проверяет: подписка может быть отклонена сервером, и это
 * нормальный ответ, а не ошибка клиента.
 *
 * Аутентификация — сессионной cookie при апгрейде. Токен в query string не
 * передаётся: query попадает в логи прокси, историю браузера и Referer.
 */

import type { ClientMessage, ServerMessage } from '@tw/shared-types';

type Listener = (message: ServerMessage) => void;

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private readonly listeners = new Set<Listener>();
  private readonly statusListeners = new Set<(s: ConnectionStatus) => void>();

  /**
   * Комнаты, на которые мы подписаны логически. Хранятся отдельно от сокета:
   * после переподключения подписки надо восстановить, иначе пользователь
   * молча перестанет получать сообщения.
   */
  private readonly desiredRooms = new Set<string>();
  /** Комнаты, на которые сервер отказал. Повторно не запрашиваем. */
  private readonly deniedRooms = new Set<string>();

  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private closedByUs = false;

  constructor(private readonly url: string = defaultWsUrl()) {}

  connect(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;

    this.closedByUs = false;
    this.emitStatus('connecting');

    const socket = new WebSocket(this.url);
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempt = 0;
      this.emitStatus('open');

      // Восстанавливаем подписки. Отказанные не повторяем: права с прошлой
      // попытки не изменились, а лишний трафик и лишние ошибки в консоли
      // мешают отлаживать настоящие проблемы.
      for (const room of this.desiredRooms) {
        if (!this.deniedRooms.has(room)) this.send({ type: 'subscribe', room: room as never });
      }

      this.heartbeatTimer = setInterval(() => this.send({ type: 'ping' }), 30_000);
    };

    socket.onmessage = (event) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }

      if (message.type === 'error' && message.code === 'FORBIDDEN' && message.room) {
        this.deniedRooms.add(message.room);
      }

      // Сервер выкинул нас из комнаты после отзыва доступа. Убираем её из
      // желаемых: иначе при следующем переподключении мы снова попросимся и
      // снова получим отказ.
      if (message.type === 'revoked') {
        this.desiredRooms.delete(message.room);
      }

      for (const listener of this.listeners) listener(message);
    };

    socket.onclose = () => {
      this.stopHeartbeat();
      this.emitStatus('closed');
      if (!this.closedByUs) this.scheduleReconnect();
    };

    socket.onerror = () => {
      // Детали ошибки браузер не отдаёт; реакция та же, что на close.
      socket.close();
    };
  }

  disconnect(): void {
    this.closedByUs = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
  }

  subscribe(room: string): void {
    this.desiredRooms.add(room);
    // Повторную попытку по отказанной комнате разрешаем только после явного
    // resetDenied() — например, когда права могли измениться.
    if (!this.deniedRooms.has(room)) this.send({ type: 'subscribe', room: room as never });
  }

  unsubscribe(room: string): void {
    this.desiredRooms.delete(room);
    this.send({ type: 'unsubscribe', room: room as never });
  }

  /** Вызывается после изменения прав: список отказов больше не актуален. */
  resetDenied(): void {
    this.deniedRooms.clear();
  }

  /**
   * Внутренний индикатор присутствия (§30 ТЗ).
   *
   * Это событие НАШЕЙ системы. В Telegram оно не транслируется: собеседник не
   * видит, что менеджер открыл чат.
   */
  presence(chatId: string, state: 'viewing' | 'replying' | 'idle'): void {
    this.send({ type: 'presence', chatId, state });
  }

  onMessage(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  private emitStatus(status: ConnectionStatus): void {
    for (const listener of this.statusListeners) listener(status);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Переподключение с экспоненциальным backoff и джиттером.
   *
   * Джиттер нужен и здесь: после перезапуска API все открытые вкладки всех
   * сотрудников попытаются переподключиться одновременно.
   */
  private scheduleReconnect(): void {
    const base = Math.min(1000 * 2 ** Math.min(this.reconnectAttempt++, 5), 30_000);
    const delay = base + Math.random() * base * 0.25;

    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }
}

function defaultWsUrl(): string {
  const configured = import.meta.env.VITE_WS_URL;
  if (configured) return configured;

  const base = import.meta.env.VITE_API_URL ?? window.location.origin;
  return `${base.replace(/^http/, 'ws')}/ws`;
}
